/**
 * @author Ketan Shetye
 * @created 2025-11-07
 * @description summary card component
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MyApplicationsService } from '../my-applications.service';
import {
  MyApplicationsData,
  SummaryData,
  SummaryListData
} from '../my-applications.component.model';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Subscription,
  tap
} from 'rxjs';
import { svgs } from 'src/assets/export.assets';
import { DataService } from 'src/app/services';
import { ActivatedRoute, Router } from '@angular/router';
import { applicantHomeRoutesNames } from 'src/app/services/mock-data/applicant-home-constants/applicant-home-constants';

@Component({
  selector: 'wm-summary-card',
  standalone: false,
  templateUrl: './summary-card.component.html',
  styleUrl: './summary-card.component.scss'
})
export class SummaryCardComponent implements OnInit, OnDestroy {
  staticSummaryDetails = {
    errorIcon: svgs.errorIcon.name
  };
  summaryList: SummaryListData[] = [];
  fetchError: string = '';
  runApi: boolean = true;
  storedActiveSummaryFilter: string = '';

  summaryDetails: SummaryData = {
    activeSummaryFilter: ''
  };
  private subscription!: Subscription;

  constructor(
    private myApplicationsService: MyApplicationsService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router
  ) {}
  ngOnInit(): void {
    //   this.subscription = this.myApplicationsService.data$.subscribe(data => {
    //     this.summaryDetails = { ...data.summaryDetails };
    //   });
    //   if (savedFilter) {
    // const currentState = this.myApplicationsService.getCurrentData();
    // this.myApplicationsService.setData({
    //   ...currentState,
    //   summaryDetails: {
    //     ...currentState.summaryDetails,
    //     activeSummaryFilter: savedFilter
    //   }
    // });
    //   }
    //   if(this.runApi){
    //     this.fetchSummaryList(clientId);
    //   }

    // });

    const clientId$ = this.route?.parent?.parent.paramMap.pipe(
      map(params => params.get('clientId')),
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      tap(() => {
        this.runApi = true;
      })
    );

    this.subscription = combineLatest([
  clientId$,
  this.myApplicationsService.data$
]).subscribe(([clientId, data]) => {

  this.storedActiveSummaryFilter =
    localStorage.getItem('storedActiveSummaryFitler') || '';

  const isInitialLoad = this.runApi;
  const isReRun = data.reRunSummaryApi === true;

  // ❌ No trigger → exit
  if (!isInitialLoad && !isReRun) return;

  // 🔒 Lock immediately to prevent duplicate calls
  this.runApi = false;

  // 🚀 Call API
  this.fetchSummaryList(clientId);

  // ✅ Reset reRun flag AFTER triggering API
  if (isReRun) {
    this.myApplicationsService.setData({
      ...this.myApplicationsService.getCurrentData(),
      reRunSummaryApi: false
    });
  }
});
  }

  ngOnDestroy(): void {
    // Prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  fetchSummaryList(cliendId: string) {
    this.fetchError = '';
    this.dataService
      .getMyApplicationSummary(cliendId)
      .pipe(
        finalize(() => {
          this.runApi = false;
          this.myApplicationsService.setData({
            ...this.myApplicationsService.getCurrentData(),
            reRunSummaryApi: false
          });
        })
      )
      .subscribe({
        next: client => {
          if (
            client.summaryCards === undefined ||
            client.summaryCards.length === 0
          ) {
            this.fetchError =
              'An error occurred while loading the summary cards. Please try again.';
          } else {
            this.summaryList = client.summaryCards as any;
            if (this.storedActiveSummaryFilter) {
              const activeSummaryCard = this.summaryList.find(
                summary => summary.command === this.storedActiveSummaryFilter
              );

              // Added by Amisha on 05/25/2026.
              // Reason: the renew summary card now routes to My Renewals instead of acting like
              // a table filter, so a stored renew selection should not be restored as an active summary filter.
              if (!activeSummaryCard || this.isRenewSummaryCard(activeSummaryCard)) {
                localStorage.removeItem('storedActiveSummaryFitler');
                return;
              }

              const isSummaryCardActive = activeSummaryCard.variant;
              if (isSummaryCardActive === 'alert') {
                // Update shared state
                const currentState: MyApplicationsData = this.myApplicationsService.getCurrentData();
                const updated: MyApplicationsData = {
                  ...currentState,
                  summaryDetails: {
                    ...currentState.summaryDetails,
                    activeSummaryFilter: this.storedActiveSummaryFilter
                  }
                };
                this.myApplicationsService.setData(updated);
                this.summaryDetails.activeSummaryFilter = this.storedActiveSummaryFilter;
              } else if (isSummaryCardActive === 'default') {
                localStorage.removeItem('storedActiveSummaryFitler');
              }
            }
          }
        },
        error: _err => {
          this.fetchError =
            'An error occurred while loading the summary cards. Please try again.';
        }
      });
  }

  // Added by Amisha on 05/25/2026.
  // Reason: only the renew summary card should redirect to My Renewals; the other alert cards
  // should keep the existing My Applications filter behavior.
  isRenewSummaryCard(summary: Pick<SummaryListData, 'title' | 'command'>): boolean {
    const title = summary?.title?.toLowerCase() || '';
    const command = summary?.command?.toLowerCase() || '';
    return title.includes('renew') || command.includes('renew');
  }

  // Added by Amisha on 05/25/2026.
  // Reason: the renew summary card needs a navigation CTA label while the other alert cards still
  // toggle between filtering the list and resetting it.
  getSummaryActionLabel(summary: SummaryListData): string {
    if (this.isRenewSummaryCard(summary)) {
      return 'View in Renewals';
    }

    return this.summaryDetails.activeSummaryFilter === summary.command
      ? 'Reset List'
      : 'View Below';
  }

  // Added by Amisha on 05/25/2026.
  // Reason: send applicants straight to the dedicated My Renewals page when they use the renew summary card.
  private navigateToRenewals(): void {
    this.router.navigate(['../', applicantHomeRoutesNames.myRenewals.route], {
      relativeTo: this.route
    });
  }

  onSummaryCardClick = (command: string, variant: string) => {
    if (variant === 'default') return;
    const selectedSummaryCard = this.summaryList.find(
      summary => summary.command === command
    );

    // Added by Amisha on 05/25/2026.
    // Reason: keep renew separate from the My Applications filter flow by navigating to My Renewals
    // instead of storing it as an active summary filter.
    if (selectedSummaryCard && this.isRenewSummaryCard(selectedSummaryCard)) {
      this.navigateToRenewals();
      return;
    }

    if (this.summaryDetails.activeSummaryFilter === command) {
      this.summaryDetails.activeSummaryFilter = '';
      localStorage.removeItem('storedActiveSummaryFitler');
      const currentState = this.myApplicationsService.getCurrentData();
      this.myApplicationsService.setData({
        ...currentState,
        summaryDetails: {
          ...currentState.summaryDetails,
          activeSummaryFilter: this.summaryDetails.activeSummaryFilter
        }
      });
    } else {
      this.summaryDetails.activeSummaryFilter = command;
      localStorage.setItem('storedActiveSummaryFitler', command);
      const currentState = this.myApplicationsService.getCurrentData();
      this.myApplicationsService.setData({
        ...currentState,
        summaryDetails: {
          ...currentState.summaryDetails,
          activeSummaryFilter: this.summaryDetails.activeSummaryFilter
        }
      });
    }
  };

  // Added by amisha on 05/07/2027 to make interactive summary cards keyboard accessible.
  // Reason: alert cards need the same Shift+Tab/Enter/Space support as their mouse interaction.
  isInteractiveSummaryCard(variant: string): boolean {
    return variant === 'alert';
  }

  // Added by amisha on 05/07/2027 to trigger summary card actions from the keyboard.
  // Reason: focused alert cards should behave the same as click when users press Enter or Space.
  onSummaryCardKeydown(event: KeyboardEvent, command: string, variant: string): void {
    if (!this.isInteractiveSummaryCard(variant)) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSummaryCardClick(command, variant);
    }
  }
}
